import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  CmtNoNumberStepDirective,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSelectComponent,
  HlmSelectOption,
  HlmSpinnerComponent,
  HlmSwitchComponent,
  HlmTextareaDirective,
  HLM_FORM_FIELD,
} from '../../../ui';
import { BrandReadonlyBannerComponent } from '../brand-readonly-banner.component';
import {
  BookkeepingConfig,
  Brand,
  BrandCreateRequest,
  BrandUpdateRequest,
  DialingCode,
  Language,
  PriceList,
  PriceListRef,
  Skill,
  Skillset,
  VatRegime,
} from '../brand.model';
import { BrandLogoComponent } from '../brand-logo.component';
import { isoForDialingCode, isoForLanguageAbbreviation } from '../country-flag.util';
import { PermissionService } from '../../../core/auth/permission.service';
import { PriceListPickerComponent } from './price-list-picker.component';
import { BrandDomainsPanelComponent } from '../domains/brand-domains-panel.component';
import { BrandNumberBlocksPanelComponent } from '../numberblocks/brand-number-blocks-panel.component';

/** Strongly-typed mirror of the form's raw value — exact field-for-field shape. */
type FormSnapshot = {
  brandName: string;
  description: string;
  defaultDomain: string;
  countryId: number | null;
  defaultCityId: number | null;
  defaultCountryPrefix: string;
  defaultCityPrefix: string;
  defaultLanguage: number | null;
  defaultSkillsetId: number | null;
  defaultSkill: string;
  defaultPriceList: PriceListRef | null;
  bookkeepingConfigurationId: number | null;
  defaultVATCountry: number | null;
  defaultServiceFeeLimit: number;
  basicFeeInAdvance: boolean;
  defaultHolidayAsSunday: boolean;
};

interface BrandFormControls {
  brandName: FormControl<string>;
  description: FormControl<string>;
  defaultDomain: FormControl<string>;
  countryId: FormControl<number | null>;
  defaultCityId: FormControl<number | null>;
  defaultCountryPrefix: FormControl<string>;
  defaultCityPrefix: FormControl<string>;
  defaultLanguage: FormControl<number | null>;
  defaultSkillsetId: FormControl<number | null>;
  defaultSkill: FormControl<string>;
  defaultPriceList: FormControl<PriceListRef | null>;
  bookkeepingConfigurationId: FormControl<number | null>;
  defaultVATCountry: FormControl<number | null>;
  defaultServiceFeeLimit: FormControl<number>;
  basicFeeInAdvance: FormControl<boolean>;
  defaultHolidayAsSunday: FormControl<boolean>;
}

@Component({
  selector: 'cmt-brand-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    HlmBtnDirective,
    HlmInputDirective,
    HlmTextareaDirective,
    HlmSelectComponent,
    HlmSwitchComponent,
    HlmSpinnerComponent,
    HlmIconComponent,
    CmtNoNumberStepDirective,
    BrandLogoComponent,
    BrandReadonlyBannerComponent,
    PriceListPickerComponent,
    BrandDomainsPanelComponent,
    BrandNumberBlocksPanelComponent,
    ...HLM_FORM_FIELD,
  ],
  templateUrl: './brand-form.component.html',
})
export class BrandFormComponent {
  /** `null` when creating a new brand. */
  readonly brand = input<Brand | null>(null);

  readonly languages = input<readonly Language[]>([]);
  readonly priceLists = input<readonly PriceList[]>([]);
  readonly dialingCodes = input<readonly DialingCode[]>([]);
  readonly skills = input<readonly Skill[]>([]);
  readonly skillsets = input<readonly Skillset[]>([]);
  readonly vatRegimes = input<readonly VatRegime[]>([]);
  readonly bookkeepingConfigs = input<readonly BookkeepingConfig[]>([]);
  /** Drives the inline spinner in the Country / Default City selects. */
  readonly dialingCodesLoading = input(false);

  readonly saving = input(false);
  readonly togglingActivation = input(false);
  /** Monotonically increasing tick — parent bumps it after a successful save
   *  so we can flash the "Saved ✓" state on the button. */
  readonly savedTick = input(0);

  readonly submitCreate = output<BrandCreateRequest>();
  readonly submitUpdate = output<BrandUpdateRequest>();
  readonly toggleActive = output<boolean>();

  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);

  /** EB-3709: gate mutating buttons for read-only users (no amie.brandManager.write). */
  protected readonly canWrite = computed(() => this.permissionService.canWrite('brandManager'));

  readonly editing = computed(() => this.brand() !== null);

  readonly form: FormGroup<BrandFormControls> = this.fb.nonNullable.group({
    brandName: this.fb.nonNullable.control('', Validators.required),
    description: this.fb.nonNullable.control(''),
    defaultDomain: this.fb.nonNullable.control('', [
      Validators.required,
      Validators.pattern(/^[a-z0-9.-]+\.[a-z]{2,}$/i),
    ]),
    countryId: this.fb.control<number | null>(null, Validators.required),
    defaultCityId: this.fb.control<number | null>(null),
    // Prefixes are hidden from the UI and derived from the picked Country/City.
    // Validity lives on those source controls — requiring the derived values
    // here produces a phantom invalid state the user can't resolve directly.
    defaultCountryPrefix: this.fb.nonNullable.control(''),
    defaultCityPrefix: this.fb.nonNullable.control(''),
    defaultLanguage: this.fb.control<number | null>(null, Validators.required),
    defaultSkillsetId: this.fb.control<number | null>(null, Validators.required),
    defaultSkill: this.fb.nonNullable.control('', Validators.required),
    defaultPriceList: this.fb.control<PriceListRef | null>(null, Validators.required),
    bookkeepingConfigurationId: this.fb.control<number | null>(null),
    defaultVATCountry: this.fb.control<number | null>(null, Validators.required),
    defaultServiceFeeLimit: this.fb.nonNullable.control(0, [Validators.required, Validators.min(0)]),
    basicFeeInAdvance: this.fb.nonNullable.control(false),
    defaultHolidayAsSunday: this.fb.nonNullable.control(false),
  });

  /** Signal mirror of the country control so cityOptions re-computes on change. */
  protected readonly countryId: Signal<number | null> = toSignal(
    this.form.controls.countryId.valueChanges,
    { initialValue: this.form.controls.countryId.value },
  );

  /**
   * OnPush + reactive-forms plays badly with `form.invalid` in templates —
   * the view doesn't re-evaluate when validity flips. Mirror the form status
   * into a signal so the Save button's disabled binding actually refreshes.
   */
  protected readonly formStatus: Signal<string> = toSignal(
    this.form.statusChanges,
    { initialValue: this.form.status },
  );
  protected readonly formInvalid = computed(() => this.formStatus() !== 'VALID');

  /** Snapshot shape — every form control value, indexed by control name. */
  private readonly formValue: Signal<FormSnapshot> = toSignal(
    this.form.valueChanges.pipe(map(() => this.form.getRawValue() as FormSnapshot)),
    { initialValue: this.form.getRawValue() as FormSnapshot },
  );

  /** Baseline of the form at hydration. Compared on submit to compute the PATCH diff. */
  private pristineSnapshot = signal<FormSnapshot | null>(null);

  /**
   * True iff the current form value differs from the hydration baseline.
   * Per-key Object.is comparison instead of JSON.stringify — order-stable,
   * doesn't conflate `null`/`undefined`, and faster on the 15-field form.
   */
  protected readonly dirty = computed(() => {
    const snap = this.pristineSnapshot();
    if (!snap) return false;
    const cur = this.formValue();
    for (const key of Object.keys(snap) as (keyof FormSnapshot)[]) {
      // defaultPriceList is an object ({ id }); the picker re-emits a fresh
      // instance with the same id, so a reference compare would report a phantom
      // change. Compare by id only — consistent with computeDiff (single
      // definition of "changed"). All other fields are primitives.
      if (key === 'defaultPriceList') {
        if (!Object.is(this.priceListId(cur.defaultPriceList), this.priceListId(snap.defaultPriceList))) {
          return true;
        }
        continue;
      }
      if (!Object.is(cur[key], snap[key])) return true;
    }
    return false;
  });

  /** Flashes true for ~1.5s after each savedTick bump; gates the "Saved ✓" label. */
  protected readonly justSaved = signal(false);

  /**
   * One-liner hint for why Save is disabled. Only surfaces states the user
   * can't figure out from the form alone: in-flight save + nothing-to-save.
   * When the form is invalid we deliberately stay silent — the disabled
   * state plus the red borders on touched fields already convey "something
   * needs fixing"; a count tooltip just duplicates the signal.
   */
  protected readonly saveDisabledReason = computed(() => {
    if (!this.canWrite()) return 'Read-only access — needs amie.brandManager.write to enable';
    if (this.saving()) return 'Saving…';
    if (this.editing() && !this.dirty() && !this.formInvalid()) return 'No changes to save';
    return '';
  });

  constructor() {
    // EB-3709: keep form enabled-state in lockstep with write permission.
    // Read-only users see populated fields but cannot mutate them — disabling
    // the parent group cascades to every control, including nested pickers.
    effect(() => {
      if (this.canWrite()) {
        this.form.enable({ emitEvent: false });
      } else {
        this.form.disable({ emitEvent: false });
      }
    });

    // Hydrate the form whenever the brand input changes.
    effect(() => {
      const b = this.brand();
      if (!b) return;

      const snap: FormSnapshot = {
        brandName: b.name,
        description: b.description ?? '',
        defaultDomain: b.domain,
        countryId: this.matchCountryId(b),
        defaultCityId: this.matchCityId(b),
        defaultCountryPrefix: b.defaultCountryPrefix ?? '',
        defaultCityPrefix: b.defaultCityPrefix ?? '',
        defaultLanguage: b.defaultLanguage,
        defaultSkillsetId: b.defaultSkillset,
        defaultSkill: b.defaultSkill,
        // Treat `0` as a real id, not "none". Some brands (e.g. Pinguin) legitimately
        // carry defaultPriceListId=0; a truthiness check would nuke prefill there.
        defaultPriceList: b.defaultPriceListId != null ? { id: b.defaultPriceListId } : null,
        bookkeepingConfigurationId: b.bookkeepingConfigurationId,
        defaultVATCountry: b.defaultVatCountryId,
        defaultServiceFeeLimit: b.defaultServiceFeeLimit ?? 0,
        basicFeeInAdvance: !!b.basicFeeInAdvance,
        defaultHolidayAsSunday: !!b.defaultHolidayAsSunday,
      };

      this.form.reset(snap, { emitEvent: false });
      // reset({ emitEvent: false }) suppresses statusChanges, so our formStatus
      // signal never hears the form flip to VALID after prefill. Force one
      // emission at the group level — children stay quiet, so the two cascade
      // subscribers below don't fire spuriously.
      this.form.updateValueAndValidity();
      this.pristineSnapshot.set({ ...snap });

      // EB-3709 H2: hydration may rebuild controls which can re-enable them;
      // reapply the permission gate after hydration to keep the form locked.
      if (!this.permissionService.canWrite('brandManager')) {
        this.form.disable({ emitEvent: false });
      }
    });

    // Derive the prefixes from the picked country/city rows. The two prefix
    // inputs are hidden in the UI — users pick a country and a city, and the
    // prefixes come along for the ride. This keeps the backend contract
    // (POST/PATCH still carries defaultCountryPrefix + defaultCityPrefix)
    // without giving the user a way to make the two disagree.
    this.form.controls.countryId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(cc => {
        if (cc === null) return;
        const country = this.dialingCodes().find(d => d.countryDialingCode.id === cc);
        if (!country) return;
        this.form.controls.defaultCountryPrefix.setValue(
          country.countryDialingCode.dialingCode,
          { emitEvent: false },
        );
        // If the currently-selected city belongs to a different country, clear it.
        const cityId = this.form.controls.defaultCityId.value;
        if (cityId !== null) {
          const city = this.dialingCodes().find(d => d.id === cityId);
          if (city && city.countryDialingCode.id !== cc) {
            this.form.controls.defaultCityId.setValue(null);
          }
        }
      });

    this.form.controls.defaultCityId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(cityId => {
        if (cityId === null) {
          this.form.controls.defaultCityPrefix.setValue('', { emitEvent: false });
          return;
        }
        const city = this.dialingCodes().find(d => d.id === cityId);
        if (!city) return;
        this.form.controls.defaultCityPrefix.setValue(city.dialingCode, { emitEvent: false });
      });

    // Flash the "Saved ✓" label for ~1.5s whenever the parent bumps savedTick.
    effect(onCleanup => {
      const tick = this.savedTick();
      if (tick === 0) return;
      this.justSaved.set(true);
      const timer = setTimeout(() => this.justSaved.set(false), 1500);
      onCleanup(() => clearTimeout(timer));
    });
  }

  // Option builders for each dropdown — keep templates thin.
  readonly countryOptions: Signal<HlmSelectOption<number>[]> = computed(() => {
    // Payload repeats each countryDialingCode once per city. Dedupe by its id,
    // then sort alphabetically. Flag comes from the SVG-backed `<cmt-flag>`
    // via the option's `flagIso` slot — text-code fallback for countries not
    // in the SVG table, so nothing renders as a blank.
    const seen = new Map<number, { countryName: string; dialingCode: string }>();
    for (const d of this.dialingCodes()) {
      if (!seen.has(d.countryDialingCode.id)) {
        seen.set(d.countryDialingCode.id, {
          countryName: d.countryDialingCode.countryName,
          dialingCode: d.countryDialingCode.dialingCode,
        });
      }
    }
    return [...seen.entries()]
      .map(([id, { countryName, dialingCode }]) => ({
        value: id,
        label: `${countryName} (+${dialingCode})`,
        flagIso: isoForDialingCode(dialingCode),
        _sortKey: countryName,
      }))
      .sort((a, b) => a._sortKey.localeCompare(b._sortKey, 'de'))
      .map(({ value, label, flagIso }) => ({ value, label, flagIso }));
  });

  readonly cityOptions: Signal<HlmSelectOption<number>[]> = computed(() => {
    // The dialingCodes payload is already the city directory — one row per
    // (city, area-code) tuple. Filter to the selected country, sort
    // alphabetically, and suffix the prefix in brackets so the user can
    // disambiguate rows with the same cityName (e.g. two Berlins with
    // different area codes) and also search by prefix.
    const countryId = this.countryId();
    const list = countryId === null
      ? this.dialingCodes()
      : this.dialingCodes().filter(d => d.countryDialingCode.id === countryId);
    return list
      .map(d => ({ value: d.id, label: `${d.cityName} (${d.dialingCode})`, _sortKey: d.cityName }))
      .sort((a, b) => a._sortKey.localeCompare(b._sortKey, 'de'))
      .map(({ value, label }) => ({ value, label }));
  });

  readonly languageOptions: Signal<HlmSelectOption<number>[]> = computed(() =>
    this.languages().map(l => ({
      value: l.id,
      label: l.name,
      flagIso: isoForLanguageAbbreviation(l.abbreviation),
    })),
  );

  readonly skillsetOptions: Signal<HlmSelectOption<number>[]> = computed(() =>
    this.skillsets().map(s => ({ value: s.id, label: s.name })),
  );

  readonly skillOptions: Signal<HlmSelectOption<string>[]> = computed(() =>
    this.skills().map(s => ({ value: s.name, label: s.name })),
  );

  readonly vatRegimeOptions: Signal<HlmSelectOption<number>[]> = computed(() =>
    this.vatRegimes().map(v => ({ value: v.id, label: v.name })),
  );

  readonly bookkeepingOptions: Signal<HlmSelectOption<number>[]> = computed(() =>
    this.bookkeepingConfigs().map(b => ({
      value: b.id,
      // Backend sends no human-readable name; the diamantId (external
      // accounting system key) is the closest thing. Fall back to `#<id>`
      // when it's null so rows without a diamantId stay distinguishable.
      label: `${b.diamantId ?? `#${b.id}`} · ${b.defaultCountry} (${b.currency})`,
    })),
  );

  onCancel(): void {
    this.router.navigate(['/admin/brands']);
  }

  onSubmit(): void {
    // EB-3709 M1: belt-and-braces — disabled buttons + form.disable() handle the
    // normal path; this guards against programmatic invocation (DevTools etc.).
    if (!this.permissionService.canWrite('brandManager')) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.editing()) {
      const diff = this.computeDiff();
      if (Object.keys(diff).length === 0) return; // nothing to save
      this.submitUpdate.emit(diff);
    } else {
      this.submitCreate.emit(this.asCreateRequest());
    }
  }

  onDeactivate(): void {
    // EB-3709 M1: belt-and-braces canWrite guard against programmatic invocation.
    if (!this.permissionService.canWrite('brandManager')) return;
    if (!this.brand() || this.togglingActivation()) return;
    this.toggleActive.emit(false);
  }

  onActivate(): void {
    // EB-3709 M1: belt-and-braces canWrite guard against programmatic invocation.
    if (!this.permissionService.canWrite('brandManager')) return;
    if (!this.brand() || this.togglingActivation()) return;
    this.toggleActive.emit(true);
  }

  /**
   * Form control name → backend key. The form keeps the old domain-flavoured
   * names for UX (`brandName`, `defaultDomain`, `defaultVATCountry`, …) while
   * submits translate to the DTO keys in BrandRequest.java. Keys present on
   * both sides with the same name (description, defaultCityId, prefixes,
   * defaultLanguage, defaultSkill, bookkeepingConfigurationId) appear here
   * too so the diff translator has a full mapping table. `defaultPriceList`
   * is absent because the picker's `{ id }` / `{ name, copyFromId }` shape
   * flattens separately into a single `defaultPriceListId`.
   */
  private static readonly FORM_TO_BACKEND_KEY: Record<string, keyof BrandCreateRequest> = {
    brandName: 'name',
    description: 'description',
    defaultDomain: 'domain',
    bookkeepingConfigurationId: 'bookkeepingConfigurationId',
    defaultVATCountry: 'vatCountryId',
    countryId: 'countryId',
    defaultServiceFeeLimit: 'usageFeeLimit',
    basicFeeInAdvance: 'payBasicFeeInAdvance',
    defaultHolidayAsSunday: 'defaultHolidayAsSunday',
    defaultCityId: 'defaultCityId',
    defaultCountryPrefix: 'defaultCountryPrefix',
    defaultCityPrefix: 'defaultCityPrefix',
    defaultLanguage: 'defaultLanguage',
    defaultSkillsetId: 'defaultSkillset',
    defaultSkill: 'defaultSkill',
  };

  /** The only price-list field the backend cares about; the picker's `{ name, copyFromId }`
   *  clone shape (and `null`) both collapse to `null` here. */
  private priceListId(ref: PriceListRef | null): number | null {
    return ref && 'id' in ref ? ref.id : null;
  }

  private computeDiff(): BrandUpdateRequest {
    const pristine = this.pristineSnapshot();
    if (!pristine) return {};
    const current = this.form.getRawValue() as FormSnapshot;
    const diff: Record<string, unknown> = {};
    // Per-key Object.is, matching the `dirty` signal: order-stable, doesn't
    // conflate null/undefined, faster than JSON.stringify on the 15-field form.
    for (const [formKey, backendKey] of Object.entries(BrandFormComponent.FORM_TO_BACKEND_KEY)) {
      const k = formKey as keyof FormSnapshot;
      if (!Object.is(current[k], pristine[k])) {
        diff[backendKey] = current[k];
      }
    }
    // Price list: PriceListRef is an object — Object.is would always be false
    // even when the user didn't touch it. Compare by the only field the
    // backend cares about (id) post-flatten. Clone shape ({ name, copyFromId })
    // isn't round-trippable yet (backend clone broken), so we skip it.
    const currentPlId = this.priceListId(current.defaultPriceList);
    const pristinePlId = this.priceListId(pristine.defaultPriceList);
    if (!Object.is(currentPlId, pristinePlId) && currentPlId !== null) {
      diff['defaultPriceListId'] = currentPlId;
    }
    return diff as BrandUpdateRequest;
  }

  private asCreateRequest(): BrandCreateRequest {
    const raw = this.form.getRawValue();
    const pl = raw.defaultPriceList;
    // `{ id }` → that id; clone shape isn't supported yet (backend clone broken).
    const defaultPriceListId = pl && 'id' in pl ? pl.id : 0;
    return {
      name: raw.brandName,
      description: raw.description,
      domain: raw.defaultDomain,
      ccProvider: null,
      bookkeepingConfigurationId: raw.bookkeepingConfigurationId,
      vatCountryId: raw.defaultVATCountry as number,
      countryId: raw.countryId as number,
      usageFeeLimit: raw.defaultServiceFeeLimit,
      payBasicFeeInAdvance: raw.basicFeeInAdvance,
      defaultHolidayAsSunday: raw.defaultHolidayAsSunday,
      defaultPriceListId,
      defaultCityId: raw.defaultCityId ?? undefined,
      defaultCountryPrefix: raw.defaultCountryPrefix,
      defaultCityPrefix: raw.defaultCityPrefix,
      defaultLanguage: raw.defaultLanguage as number,
      defaultSkillset: raw.defaultSkillsetId as number,
      defaultSkill: raw.defaultSkill,
    };
  }

  /**
   * The `Brand` payload exposes country *name* + *prefix*, not the LANDESVORWAHLEN id.
   * Country names in dialing-codes are German ("Deutschland") while the Brand uses
   * English ("Germany"), so we can only match on the numeric dialing code.
   */
  private matchCountryId(b: Brand): number | null {
    if (!b.defaultCountryPrefix) return null;
    const match = this.dialingCodes().find(
      d => d.countryDialingCode.dialingCode === b.defaultCountryPrefix,
    );
    return match ? match.countryDialingCode.id : null;
  }

  /**
   * Resolve the city-entry id from the brand's country + city prefix. Tiebreak
   * on `defaultCityName` when several cities share an area code; fall back to
   * the prefix-only match if the name doesn't line up (e.g. English vs German
   * spelling). Returns null if the dialing codes haven't loaded yet or no row
   * matches.
   */
  private matchCityId(b: Brand): number | null {
    if (!b.defaultCountryPrefix || !b.defaultCityPrefix) return null;
    const inCountry = this.dialingCodes().filter(
      d => d.countryDialingCode.dialingCode === b.defaultCountryPrefix
        && d.dialingCode === b.defaultCityPrefix,
    );
    if (inCountry.length === 0) return null;
    if (inCountry.length === 1) return inCountry[0].id;
    const byName = b.defaultCityName
      ? inCountry.find(d => d.cityName === b.defaultCityName)
      : undefined;
    return (byName ?? inCountry[0]).id;
  }
}
